<script lang="ts" setup>
import { useProjectStore } from '@/store/projectStore';
import { ALLOWED_IMAGE_TYPES } from '@/utils/helpers/imageCompressionHelper';

interface Props {
  projectId?: string | null;
  // A file picked before, e.g. when the creation wizard comes back to its
  // data step; read once on mount.
  initialFile?: File | null;
}

const props = withDefaults(defineProps<Props>(), {
  projectId: null,
  initialFile: null,
});
const emit = defineEmits<{
  (e: 'image-file', file: File | null): void;
  (e: 'delete-image', value: boolean): void;
}>();

const projectStore = useProjectStore();
const message = useAlerts();

const fileInputRef = ref<HTMLInputElement | null>(null);
const imageFile = ref<File | null>(props.initialFile);
const pendingPreviewUrl = ref<string | null>(
  props.initialFile ? URL.createObjectURL(props.initialFile) : null,
);
const deleteImage = ref(false);

const savedBlobUrl = computed(() =>
  props.projectId ? projectStore.imageUrls[props.projectId] : undefined,
);
const previewUrl = computed(() => {
  if (pendingPreviewUrl.value) {
    return pendingPreviewUrl.value;
  }
  if (deleteImage.value) {
    return null;
  }

  return savedBlobUrl.value ?? null;
});
const hasImage = computed(() => !!previewUrl.value);

onMounted(() => {
  if (props.projectId) {
    projectStore.loadProjectImage(props.projectId);
  }
});

onUnmounted(() => {
  if (pendingPreviewUrl.value) {
    URL.revokeObjectURL(pendingPreviewUrl.value);
  }
});

function onUploadClick() {
  fileInputRef.value?.click();
}

async function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) {
    return;
  }

  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    message.error($t('project.image.invalidType'));
    input.value = '';

    return;
  }

  if (pendingPreviewUrl.value) {
    URL.revokeObjectURL(pendingPreviewUrl.value);
  }
  imageFile.value = file;
  pendingPreviewUrl.value = URL.createObjectURL(file);
  deleteImage.value = false;
  emit('image-file', file);
  emit('delete-image', false);
  input.value = '';
}

function onClearClick() {
  if (imageFile.value) {
    if (pendingPreviewUrl.value) {
      URL.revokeObjectURL(pendingPreviewUrl.value);
    }
    imageFile.value = null;
    pendingPreviewUrl.value = null;
    emit('image-file', null);
  } else {
    deleteImage.value = true;
    emit('delete-image', true);
  }
}
</script>

<template>
  <div class="image-upload">
    <input
      ref="fileInputRef"
      type="file"
      accept="image/jpeg,image/png,image/webp,image/gif"
      class="hidden-input"
      @change="onFileChange"
    />

    <div class="image-upload-card" @click="onUploadClick">
      <div class="image-thumb">
        <img
          v-if="previewUrl"
          :src="previewUrl"
          class="image-thumb-img"
          alt=""
        />
        <UiIcon
          v-else
          name="Image"
          :size="14"
          class="image-thumb-placeholder"
        />
      </div>

      <div class="image-upload-info">
        <span class="image-upload-title">
          {{ $t('project.image.clickToUpload') }}
        </span>
      </div>
      <UiIcon
        v-if="hasImage"
        class="image-upload-clear"
        name="XIcon"
        @click.stop="onClearClick"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.image-upload {
  width: 100%;
}

.hidden-input {
  display: none;
}

.image-upload-card {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  padding: px(5) var(--space-10);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--border-radius-6);
  cursor: pointer;
  min-height: px(44);

  &:hover {
    background: var(--color-gray-100);
  }
}

.image-thumb {
  width: px(22);
  height: px(22);
  border-radius: px(3);
  overflow: hidden;
  background: var(--color-gray-200);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-thumb-placeholder {
  color: var(--color-gray-400);
}

.image-upload-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.image-upload-title {
  font-size: var(--font-size-13);
  font-weight: var(--font-weight-regular);
  color: var(--color-gray-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.image-upload-clear {
  display: inline-flex;
  align-items: center;
  padding: var(--space-4);
  border: none;
  background: transparent;
  color: var(--color-gray-500);
  cursor: pointer;
  border-radius: var(--border-radius-4);
  flex-shrink: 0;

  &:hover:not(:disabled) {
    color: var(--color-gray-800);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
